import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

function toQueryString(params) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  return query.toString();
}

function getErrorMessage(payload, fallback) {
  if (!payload) {
    return fallback;
  }
  if (typeof payload === 'string') {
    return payload;
  }
  return payload.error || payload.message || fallback;
}

export function useAccountApi() {
  const { getIdToken } = useAuth();

  const request = useCallback(
    async (path, options = {}) => {
      const token = typeof getIdToken === 'function' ? await getIdToken() : null;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      };

      const response = await fetch(`${API_ENDPOINT}${path}`, {
        ...options,
        headers,
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        throw new Error(getErrorMessage(payload, 'Request failed.'));
      }

      return payload;
    },
    [getIdToken]
  );

  const getAccount = useCallback(() => request('/api/account'), [request]);

  const getTransactions = useCallback(
    (params) => {
      const query = toQueryString(params);
      return request(`/api/account/transactions${query ? `?${query}` : ''}`);
    },
    [request]
  );

  const startPurchase = useCallback(
    ({ bundleId, idempotencyKey }) =>
      request('/api/account/purchase', {
        method: 'POST',
        body: JSON.stringify({ bundle_id: bundleId }),
        headers: {
          'X-Idempotency-Key': idempotencyKey,
        },
      }),
    [request]
  );

  const confirmPayment = useCallback(
    ({ sessionId, idempotencyKey }) =>
      request('/api/payments/confirm', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
        headers: {
          ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
        },
      }),
    [request]
  );

  return {
    getAccount,
    getTransactions,
    startPurchase,
    confirmPayment,
  };
}
